import { defaultOptions, fetchJson } from '@fv/client-core'
import type { Load } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'
import { type DTO, encodeUriAll, stringifyQueryString } from '@fv/models/core'

import { apiUri } from './constants'
import type { AdminLoad } from './types/admin-load'

export type MagicLinkOpts = Pick<Load, '_id' | 'status' | 'createdBy'>

export const buildLoadLink = (opts: MagicLinkOpts) => {
  return opts.status === 'booked'
    ? `details/${opts._id}`
    : `search?text=${opts._id}`
}

export function friendlyDate(
  date?: string | Date,
  format = 'MM/DD/YYYY hh:mm A',
  fallback = 'N/A',
) {
  if (!date) return fallback

  return fvDateUtc(date).local().format(format)
}

export const buildLogRocketSearchLink = (urlContains: string) => {
  const filter = [
    {
      id: 'BJMKN5l_3',
      type: 'timeOnPage',
      params: {
        url: {
          operator: { name: 'contains', type: 'CONTAINS', hasStrings: true },
          strings: [urlContains],
        },
        time: {
          operator: {
            name: 'for more than',
            type: 'TIME_GT',
            hasStrings: true,
            category: 'time',
            value: '1s',
          },
          strings: ['1s'],
        },
      },
    },
  ]

  return `https://app.logrocket.com/d6lkaq/freightview-production/sessions?filters=${encodeURIComponent(
    encodeURIComponent(JSON.stringify(filter)),
  )}`
}

export function getAdminLoadStatus(load: AdminLoad) {
  let status: string = load.status

  if (
    status === 'booked' &&
    (load.dispatch?.status === 'ok' ||
      load.dispatch?.status === 'not-requested' ||
      load.isLegacy)
  ) {
    status = 'dispatched'
  }

  if (
    status === 'booked' &&
    load.dispatch?.status === 'error' &&
    load.dispatch.errors?.some(e => e.type === 'validation')
  ) {
    status = 'whitelist error'
  }

  if (
    status === 'booked' &&
    load.dispatch?.status === 'pending' &&
    load.dispatch?.method === 'internal'
  ) {
    status = 'needs dispatch'
  }

  if (status === 'booked' && load.dispatch?.status === 'pending') {
    status = 'pending confirmation'
  }

  return status
}

const groupParamsByKey = (params: URLSearchParams) =>
  [...params.entries()].reduce(
    (acc, tuple) => {
      // getting the key and value from each tuple
      const [key, val] = tuple
      if (Object.hasOwn(acc, key)) {
        // if the current key is already an array, we'll add the value to it
        if (Array.isArray(acc[key])) {
          acc[key] = [...acc[key], val]
        } else {
          // if it's not an array, but contains a value, we'll convert it into an array
          // and add the current value to it
          acc[key] = [acc[key], val]
        }
      } else {
        // plain assignment if no special case is present
        acc[key] = val
      }

      return acc
    },
    {} as Record<string, any>,
  )

export const parseParams = <T>(search: string): T => {
  const params = new URLSearchParams(search)

  return groupParamsByKey(params) as T
}

interface FetchOptions {
  query?: Record<string, any> | string
  body?: Record<string, any>
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH'
}
export async function adminFetch<T>(
  path: string,
  options?: FetchOptions,
): Promise<DTO<T>> {
  const endpoint = `${apiUri}${path}`
  const response = await fetchJson(
    endpoint,
    defaultOptions({
      method: 'GET',
      ...options,
      ...(options?.body && { body: JSON.stringify(options.body) }),
      credentials: 'include',
    }),
  )
  if (response.ok) return response.json
  throw response.errorMessage
}

export async function adminFetchWithQueryBrackets<T>(
  path: string,
  options?: FetchOptions,
) {
  return adminFetch<T>(path, {
    ...options,
    ...(options?.query &&
      typeof options.query === 'object' && {
        query: stringifyQueryString(options.query),
      }),
  })
}

export async function adminFetchRaw(
  uri: string,
  options?: RequestInit & { query?: Record<string, any> },
) {
  return fetch(`${apiUri}${uri}`, {
    ...defaultOptions(),
    credentials: 'include',
    ...options,
  })
}

type LinkProps = {
  query: string
  startTime?: string
  endTime?: string
  project?: string
  duration?: `${string}${'M' | 'H'}`
  summaryFields?: string[]
}

export function buildGcpLogLink({
  query,
  startTime,
  endTime,
  duration,
  summaryFields,
  project,
}: LinkProps): string {
  project = project || 'freightview-1470414526790'

  const attrs = {
    query: query
      .trim()
      .split('\n')
      .map(q =>
        q
          .replace(/\s+/g, '')
          .replace(/=~"(.+)"/g, (m, g1) => `=~"${encodeUriAll(g1)}"`),
      )
      .join('%0A'),

    ...(summaryFields?.length && {
      summaryFields: summaryFields
        .map(s => encodeUriAll(s.replace(/\./g, '/')))
        .join(','),
    }),
    ...(startTime && {
      startTime: fvDateUtc(startTime).toISOString(),
    }),
    ...(endTime && {
      endTime: fvDateUtc(endTime).toISOString(),
    }),
    ...(endTime && {
      endTime: fvDateUtc(endTime).toISOString(),
    }),
    ...(duration && {
      duration: `PT${duration}`,
    }),
  }

  const attrsStr = Object.entries(attrs)
    .map(([key, value]) => `${key}=${value}`)
    .join(';')

  return `https://console.cloud.google.com/logs/query;${attrsStr}?project=${project}`
}

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
  })
